import { useRef, useCallback } from "react"
import { Delete } from "lucide-react"

interface NumberPadProps {
  loading: boolean
  canLogin: boolean
  onNumberPress: (num: string) => void
  onBackspace: () => void
  onLogin: () => void
}

export function NumberPad({
  loading,
  canLogin,
  onNumberPress,
  onBackspace,
  onLogin,
}: NumberPadProps) {
  const numberKeys = ["1", "2", "3", "4", "5", "6", "7", "8", "9"]
  const lastPressTime = useRef<number>(0)
  const DEBOUNCE_MS = 100

  // Debounced number press to prevent double-taps on touch devices
  const handleNumberPress = useCallback((num: string) => {
    const now = Date.now()
    if (now - lastPressTime.current < DEBOUNCE_MS) return
    lastPressTime.current = now
    onNumberPress(num)
  }, [onNumberPress])

  const numberButtonClass =
    "h-[72px] rounded-xl bg-white border border-slate-200/80 shadow-[0_1px_2px_rgba(15,40,71,0.04),0_2px_6px_rgba(15,40,71,0.04)] text-2xl font-semibold text-navy-900 transition-all duration-150 ease-out hover:border-navy-300 hover:bg-navy-50/50 hover:shadow-[0_2px_4px_rgba(15,40,71,0.08),0_4px_12px_rgba(15,40,71,0.08)] hover:-translate-y-px active:translate-y-0 active:shadow-[0_1px_2px_rgba(15,40,71,0.04)] active:bg-navy-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:-translate-y-0 disabled:hover:shadow-[0_1px_2px_rgba(15,40,71,0.04)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 focus-visible:ring-offset-2"

  return (
    <div className="grid grid-cols-3 gap-2.5" role="group" aria-label="PIN digit entry">
      {/* Number buttons 1-9 */}
      {numberKeys.map((num) => (
        <button
          key={num}
          type="button"
          onClick={() => handleNumberPress(num)}
          disabled={loading}
          className={numberButtonClass}
        >
          {num}
        </button>
      ))}

      {/* Bottom row: <, 0, LOGIN (per CEO wireframe) */}
      <button
        type="button"
        onClick={onBackspace}
        disabled={loading}
        aria-label="Backspace"
        className="h-[72px] rounded-xl bg-slate-100 border border-slate-200/80 shadow-[0_1px_2px_rgba(15,40,71,0.04)] text-xl font-semibold text-slate-500 transition-all duration-150 hover:bg-slate-200 hover:text-slate-700 hover:-translate-y-px active:translate-y-0 active:bg-slate-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:-translate-y-0 flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 focus-visible:ring-offset-2"
      >
        <Delete className="h-6 w-6" aria-hidden="true" />
      </button>

      <button
        type="button"
        onClick={() => handleNumberPress("0")}
        disabled={loading}
        className={numberButtonClass}
      >
        0
      </button>

      <button
        type="button"
        onClick={onLogin}
        disabled={loading || !canLogin}
        className={`h-[72px] rounded-xl font-bold text-sm uppercase tracking-[0.15em] transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/60 focus-visible:ring-offset-2 ${
          canLogin && !loading
            ? "bg-linear-to-br from-navy-700 to-navy-900 text-white shadow-[0_4px_14px_rgba(26,62,106,0.35)] hover:shadow-[0_6px_20px_rgba(26,62,106,0.45)] hover:-translate-y-px active:translate-y-0"
            : "bg-slate-200 text-slate-500 cursor-not-allowed"
        }`}
      >
        {loading ? (
          <div className="flex items-center justify-center gap-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
          </div>
        ) : (
          "Login"
        )}
      </button>
    </div>
  )
}
